import { useEffect, useState } from 'react';

import { useIsLoggedIn } from '../Store/user/hooks';
import {
	getBannerDismissedVersion,
	getCtaContent,
	getPendingLoginBanner,
	getPopupDismissedVersionLocal,
	isCtaContentLoaded,
	loadCtaContentFromApi,
	parsePopupDismissedVersion,
	subscribeToCtaContent,
} from './ctaStorage';

import type { CtaBannerId, CtaContent } from './types';
import { getCtaBannerContent, getCtaBannerVersion } from './types';
import { isWithinCtaSchedule } from './scheduleUtils';

export const useCtaContent = (): CtaContent => {
	const authenticated = useIsLoggedIn();
	const [content, setContent] = useState(getCtaContent);

	useEffect(() => {
		const unsubscribe = subscribeToCtaContent(() => setContent(getCtaContent()));
		return unsubscribe;
	}, []);

	useEffect(() => {
		if (!authenticated) return undefined;
		loadCtaContentFromApi().catch(() => undefined);
		return undefined;
	}, [authenticated]);

	return content;
};

export const useAnnouncementBannerVisible = (
	content: CtaContent,
	bannerId: CtaBannerId,
	userId: number | undefined,
	authenticated: boolean,
): boolean => {
	const [visible, setVisible] = useState(false);
	const { text, scheduleStart, scheduleEnd } = getCtaBannerContent(content, bannerId);

	useEffect(() => {
		const update = () => {
			if (!authenticated || !userId || !isCtaContentLoaded() || !text.trim()) {
				setVisible(false);
				return;
			}
			if (!isWithinCtaSchedule({ start: scheduleStart, end: scheduleEnd })) {
				setVisible(false);
				return;
			}
			const pendingLogin = getPendingLoginBanner(userId, bannerId);
			const dismissedVersion = getBannerDismissedVersion(userId, bannerId);
			const bannerVersion = getCtaBannerVersion(content, bannerId);
			setVisible(pendingLogin && dismissedVersion < bannerVersion);
		};
		update();
		return subscribeToCtaContent(update);
	}, [
		authenticated,
		bannerId,
		scheduleEnd,
		scheduleStart,
		text,
		content.bannerVersion,
		content.bannerTwoVersion,
		userId,
	]);

	return visible;
};

export const useIsPopupPermanentlyDismissed = (
	content: CtaContent,
	userId: number | undefined,
	authenticated: boolean,
	popupDismissedVersion: string | undefined,
): boolean => {
	const [dismissed, setDismissed] = useState(false);

	useEffect(() => {
		if (!authenticated || !userId || !isCtaContentLoaded() || !content.popupTitle.trim()) {
			setDismissed(false);
			return;
		}
		const dismissedVersion = Math.max(
			parsePopupDismissedVersion(popupDismissedVersion),
			userId ? getPopupDismissedVersionLocal(userId) : 0,
		);
		setDismissed(dismissedVersion >= content.version);
	}, [authenticated, content.popupTitle, content.version, popupDismissedVersion, userId]);

	return dismissed;
};
